import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { IRenameFolderBody, StorageAPI, StorageFoldersAPI } from '@makyoapp/makyo-sdk/api/Storage'
import { IFolderBrief } from '@makyoapp/makyo-sdk/models/Files'
import { Folder as FolderIcon, OpenedFolder } from '@makyoapp/makyo-icon/ficon'
import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, Checkbox, Field, Label, Loading, ModalGrid, SimpleInput } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useStorageContext } from '@/context/StorageContext'
import { StorageFolderMoreDropdown } from '@/pages/org/storage/components/StorageFolderMoreDropdown'

/**
 * StorageFoldersSection
 */
export function StorageFoldersSection() {
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Access Storage Context
  const {
    refreshStorage,
    currentFolderId,
    setCurrentFolderId,
    storageFolders,
    isLoadingFolders,
    checkedFolders,
    setCheckedFolders,
    setCheckedFiles,
  } = useStorageContext()

  // Derived
  const isAllSelected = checkedFolders.length === storageFolders.length

  const { currentOrg } = useCurrentOrgContext()
  const currentOrgId = currentOrg?.id || ''

  // Use the explicit interface for the state
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [folderToRename, setFolderToRename] = useState<{ id: string; name: string } | null>(null)
  const [newRenameValue, setNewRenameValue] = useState('')
  const [isRenamingFolder, setIsRenamingFolder] = useState(false)
  const [isDeletingFolder, setIsDeletingFolder] = useState(false)
  const [draggedFolder, setDraggedFolder] = useState<string | null>(null)
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null)

  // Nested folders
  const [folderPath, setFolderPath] = useState<Array<{ id: string; name: string }>>([])

  // Drag & Drop
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, folderId: string) => {
    setDraggedFolder(folderId)

    e.dataTransfer.setData('text/plain', folderId) //! File
    e.dataTransfer.setData('text/data-type', 'folder') //! Folder
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, folderId: string) => {
    e.preventDefault()

    if (draggedFolder === folderId) {
      return
    }

    setDragOverFolder(folderId)

    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragLeave = () => {
    setDragOverFolder(null)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, folderId: string) => {
    e.preventDefault()

    setDragOverFolder(null)

    try {
      const dataType = e.dataTransfer.getData('text/data-type')

      if (dataType === 'files') {
        const fileIds = JSON.parse(e.dataTransfer.getData('text/plain')) as string[]

        await StorageAPI.ReqMoveFilesToFolder(folderId, {
          _OrgID: currentOrgId,
          _Files: fileIds,
        })

        toasted(t(`${fileIds.length} item${fileIds.length > 1 ? 's' : ''} moved successfully`))
        //
      } else if (dataType === 'folder') {
        const sourceFolderId = e.dataTransfer.getData('text/plain')

        if (sourceFolderId === folderId) {
          return
        }

        await StorageFoldersAPI.ReqMove(sourceFolderId, folderId)

        toasted(t(`Folder moved successfully`))
      }
      await refreshStorage()

      setCheckedFiles([])
      setCheckedFolders([])
      //
    } catch (error) {
      console.error('Error in handleDrop:', error)
    }
  }

  // Handler untuk drag end (jika drag dibatalkan)
  const handleDragEnd = () => {
    setDraggedFolder(null)
    setDragOverFolder(null)
  }

  //! Create new folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    try {
      setIsSubmitting(true)

      const orgId = currentOrg?.id

      if (!orgId) {
        return
      }

      const folderData = {
        _OrgID: orgId,
        _Name: newFolderName.trim(),
        // Only include _ParentID if we're in a subfolder
        ...(currentFolderId ? { _ParentID: currentFolderId } : {}),
      }

      const response = await StorageFoldersAPI.ReqCreate(folderData)

      if (response && response._id) {
        // Close the modal and reset the form
        setIsNewFolderModalOpen(false)
        setNewFolderName('')

        // Refresh the storage data to reflect the new folder
        refreshStorage()
        toasted(t('Folder created successfully'))
      } else {
        console.warn('Folder creation response does not contain expected ID')
      }
    } catch (error) {
      console.error('Failed to create folder: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSelectFolder = (folder: IFolderBrief) => {
    const isSelected = !!checkedFolders.find((f) => f._id === folder._id)

    if (isSelected) {
      setCheckedFolders(checkedFolders.filter((item) => item._id !== folder._id))
    } else {
      setCheckedFolders([...checkedFolders, folder])
    }
  }

  const handleSelectAllFolders = () => {
    setCheckedFolders(isAllSelected ? [] : storageFolders)
  }

  //! Rename folder
  const handleRenameFolder = (id: string, currentName: string) => {
    setFolderToRename({ id, name: currentName })
    setNewRenameValue(currentName)
    setIsRenameModalOpen(true)
  }

  const applyRename = async () => {
    if (!folderToRename || !newRenameValue.trim()) return

    try {
      setIsRenamingFolder(true)

      const renameData: IRenameFolderBody = {
        _Name: newRenameValue.trim(),
      }

      await StorageFoldersAPI.ReqRename(folderToRename.id, renameData)

      await refreshStorage()

      // Reset state
      setIsRenameModalOpen(false)
      setFolderToRename(null)
      setNewRenameValue('')
      //
    } catch (error) {
      console.error('Error renaming folder:', error)
    } finally {
      setIsRenamingFolder(false)
    }
  }

  // Delete folder
  const handleDeleteFolder = async (id: string) => {
    try {
      setIsDeletingFolder(true)

      await StorageFoldersAPI.ReqDelete(id, '')
      await refreshStorage()
      setCheckedFolders([])
      toasted('Folder deleted successfully')
      //
    } catch (error) {
      toasted('Failed to delete folder: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsDeletingFolder(false)
    }
  }

  // Handle opening a folder (navigating into it)
  const handleOpenFolder = (folder: { id: string; name: string }) => {
    setFolderPath((prev) => [...prev, folder])
    setCurrentFolderId(folder.id)
  }

  return (
    <>
      <section id='storage-folders' className='mb-8 space-y-6'>
        {/* Breadcrumb */}
        <div id='storage-folders-breadcrumb' className='flex items-center gap-2.5 text-light'>
          <div
            role='button'
            className='hover:underline flex items-center gap-1.5 cursor-pointer'
            onClick={() => {
              setCurrentFolderId('')
              setFolderPath([])
            }}
          >
            <OpenedFolder width={15} height={15} />
            <span>Root</span>
          </div>

          {folderPath.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <div>/</div>

              {index === folderPath.length - 1 ? (
                <div className='flex items-center gap-1.5'>
                  <OpenedFolder width={15} height={15} />
                  <span>{folder.name}</span>
                </div>
              ) : (
                <div
                  role='button'
                  className='hover:underline flex items-center gap-1 cursor-pointer'
                  onClick={() => {
                    const newPath = folderPath.slice(0, index + 1)
                    setFolderPath(newPath)
                    setCurrentFolderId(folder.id)
                  }}
                >
                  <OpenedFolder width={15} height={15} />
                  <span>{folder.name}</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Folders Section Header */}
        <div id='storage-folders-header' className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            {currentFolderId ? (
              <h2 className='text-m-l'>
                Folders in <span className='font-semibold'>{folderPath[folderPath.length - 1]?.name || ''}</span>
              </h2>
            ) : (
              <h2 className='text-m-l font-semibold'>Folders</h2>
            )}

            {storageFolders.length > 0 && (
              <div className='flex items-center gap-2'>
                <Checkbox
                  labelClassName='[&>div]:size-4'
                  label={`${checkedFolders.length > 0 ? `Selected ${checkedFolders.length} folder(s)` : 'Select all folders'}`}
                  checked={checkedFolders.length === storageFolders.length && storageFolders.length > 0}
                  onClick={() => handleSelectAllFolders()}
                />
              </div>
            )}
          </div>

          <Button onClick={() => setIsNewFolderModalOpen(true)} className='flex items-center gap-2'>
            <Plus16 />
            New Folder
          </Button>
        </div>

        {/* Folders List */}

        <div id='storage-folders-list'>
          {isLoadingFolders ? (
            <div className='w-full min-h-[100px] flex justify-center items-center'>
              <Loading fullHeight={false} />
            </div>
          ) : storageFolders.length > 0 ? (
            <div className='grid grid-cols-3 gap-4'>
              {storageFolders.map((folder) => {
                const isDraggedOver = dragOverFolder === folder._id
                return (
                  <div
                    key={folder._id}
                    className={clsx(
                      `cursor-pointer group p-4 relative rounded-sm bg-white dark:bg-slate-800 hover:bg-zinc-50 dark:hover:bg-slate-700 border-[1.5px]`,
                      isDraggedOver ? 'border-primary-500 opacity-80' : 'bdr'
                    )}
                    draggable
                    onDragStart={(e) => handleDragStart(e, folder._id)}
                    onDragOver={(e) => handleDragOver(e, folder._id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, folder._id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleOpenFolder({ id: folder._id, name: folder._Name })}
                  >
                    <div
                      className='absolute top-2 right-2'
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <Checkbox
                        labelClassName='[&>div]:size-4.5'
                        checked={!!checkedFolders.find((f) => f._id === folder._id)}
                        onClick={() => {
                          handleSelectFolder(folder)
                        }}
                      />
                    </div>

                    <div className='-ml-1 pb-3 pr-5 flex items-center gap-2 overflow-hidden'>
                      <span className='shrink-0'>
                        <FolderIcon width={20} height={20} />
                      </span>
                      <span className='truncate'>{folder._Name}</span>
                    </div>

                    {/*<div className="text-sm text-slate-400">{folder.files} files</div>*/}
                    {/*<div className="text-xs text-slate-500">Modified {folder.lastModified}</div>*/}
                    <div className='mb-6'></div>
                    <div className='absolute bottom-2 right-2' onClick={(e) => e.stopPropagation()}>
                      <StorageFolderMoreDropdown
                        folderId={folder._id}
                        folderName={folder._Name}
                        onRename={handleRenameFolder}
                        onDelete={handleDeleteFolder}
                        // onMove={handleMoveFolder}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      </section>

      {/** Create/New Folder Modal */}
      <ModalGrid
        show={isNewFolderModalOpen}
        title='Create New Folder'
        onCancel={() => {
          setIsNewFolderModalOpen(false)
          setNewFolderName('')
        }}
        onConfirm={handleCreateFolder}
        disabled={!newFolderName.trim() || isSubmitting || !!errorMessage}
        isLoading={isSubmitting}
        primaryTrailing={isSubmitting ? 'Creating...' : 'Create Folder'}
        showHeader={true}
        showCancel={true}
        modalId='create-folder-modal'
        submitId='create-folder-submit'
        cancelId='create-folder-cancel'
        bodyProps={{
          className: 'p-6',
        }}
      >
        <Field defaultSize={false} noPad className='min-h-[3rem]'>
          <Label defaultSize={false} className={clsx('lbl-sm')}>
            {t('Folder Name')}
          </Label>
          <SimpleInput
            type='text'
            error={errorMessage}
            withCounterConfig={{ min: 0, max: 20 }}
            value={newFolderName}
            onChange={(e) => {
              if (e.target.value.length <= 20) {
                setNewFolderName(e.target.value)
                setErrorMessage('')
              } else {
                setErrorMessage(t('Folder name must be less than 20 characters'))
              }
            }}
            placeholder='Enter new folder name'
            // disabled={isRenamingFolder}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName.trim() && !isSubmitting) {
                handleCreateFolder()
              }
            }}
          />
        </Field>
      </ModalGrid>

      {/** Rename Folder Modal */}
      <ModalGrid
        show={isRenameModalOpen}
        title='Rename Folder'
        onCancel={() => {
          setIsRenameModalOpen(false)
          setNewRenameValue('')
        }}
        onConfirm={applyRename}
        disabled={!newRenameValue.trim() || isRenamingFolder || !!errorMessage}
        isLoading={isRenamingFolder}
        primaryTrailing={isRenamingFolder ? 'Renaming...' : 'Rename'}
        showHeader={true}
        showCancel={true}
        modalId='rename-folder-modal'
        submitId='rename-folder-submit'
        cancelId='rename-folder-cancel'
        bodyProps={{
          className: 'p-6',
        }}
      >
        <Field defaultSize={false} noPad className='min-h-[3rem]'>
          <Label defaultSize={false} className={clsx('lbl-sm')}>
            {t('Folder Name')}
          </Label>
          <SimpleInput
            type='text'
            error={errorMessage}
            withCounterConfig={{ min: 0, max: 20 }}
            value={newRenameValue}
            onChange={(e) => {
              if (e.target.value.length <= 20) {
                setNewRenameValue(e.target.value)
                setErrorMessage('')
              } else {
                setErrorMessage(t('Folder name must be less than 20 characters'))
              }
            }}
            placeholder='Enter new folder name'
            disabled={isRenamingFolder}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newRenameValue.trim() && !isRenamingFolder) {
                applyRename()
              }
            }}
          />
        </Field>
      </ModalGrid>
    </>
  )
}
